/**
 * Auf- und Zuklappen der Kategorien in der Navigation (REQ-20 §3.1).
 *
 * Der Zustand steht je Kategorie in `localStorage` unter `iwm-nav-groups` als
 * JSON-Objekt `{ [kategorieId]: boolean }` und übersteht damit einen Neuaufbau
 * der Seite. Standard ist **aufgeklappt**: Nur zugeklappte Gruppen müssen
 * überhaupt vermerkt werden, ein leerer Speicherstand zeigt alles.
 */

import { useCallback } from 'react';

import { useLocalStorage, type LocalStorageCodec } from './useLocalStorage';

export const NAV_GROUPS_STORAGE_KEY = 'iwm-nav-groups';

export type NavGroupsMap = Record<string, boolean>;

/** Eigenschaften statt Methoden: Der Rückgabewert wird destrukturiert. */
export interface NavGroupsState {
  /** Standard `true` — eine Kategorie ohne Eintrag gilt als aufgeklappt. */
  isExpanded: (categoryId: number) => boolean;
  toggle: (categoryId: number) => void;
}

/**
 * `JSON.parse` liefert für jeden gültigen JSON-Text einen Wert — auch für
 * `"3"` oder `"[]"`. Alles, was kein Objekt aus Wahrheitswerten ist, wird
 * deshalb verworfen; `useLocalStorage` übernimmt dann den Anfangswert.
 */
const navGroupsCodec: LocalStorageCodec<NavGroupsMap> = {
  parse: (raw: string): NavGroupsMap => {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      throw new Error(`Unerwarteter Zustand der Navigationsgruppen: ${raw}`);
    }

    const entries = Object.entries(value).filter(
      (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
    );
    return Object.fromEntries(entries);
  },
};

export function useNavGroups(): NavGroupsState {
  const [groups, store] = useLocalStorage<NavGroupsMap>(NAV_GROUPS_STORAGE_KEY, {}, navGroupsCodec);

  const isExpanded = useCallback(
    (categoryId: number): boolean => groups[String(categoryId)] ?? true,
    [groups],
  );

  const toggle = useCallback(
    (categoryId: number): void => {
      const key = String(categoryId);
      store({ ...groups, [key]: !(groups[key] ?? true) });
    },
    [groups, store],
  );

  return { isExpanded, toggle };
}
